import { useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useToast } from 'components/hooks/useToast';

import ApprovalSection from './ApprovalSection';
import { CancelConfirmationModal } from './CancelConfirmationModal';
import FooterSection from './FooterSection';
import NotesSection from './NotesSections';
import { saveDisciplinaryActionsSettingsApi, useGetDisciplinaryActionsSettings } from './dataHooks';

const DisciplinaryActionsSettings = () => {
  const { data, isLoading, isError } = useGetDisciplinaryActionsSettings();
  const [notesList, setNotesList] = useState([]);
  const [isApprovalRequired, setIsApprovalRequired] = useState(false);
  const [autoApproveDays, setAutoApproveDays] = useState(0);
  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (data) {
      const settings = {
        noteTypes: data.noteTypes || [],
        isApprovalRequired: data.isApprovalRequired,
        autoApproveDays: data.autoApproveDays,
      };

      setInitialSettings(settings);

      setNotesList(settings.noteTypes);
      setIsApprovalRequired(settings.isApprovalRequired);
      setAutoApproveDays(settings.autoApproveDays);
    }
  }, [data]);

  const handleSave = () => {
    const payload = {
      noteTypes: notesList || [],
      isApprovalRequired,
      autoApproveDays,
    };
    saveDisciplinaryActionsSettingsApi(payload)
      .then(() => {
        toast({
          title: 'Disciplinary actions settings updated successfully',
          status: 'success',
        });

        setInitialSettings(payload);
      })
      .catch(() => {
        toast({
          title: 'Error occurred while updating the settings',
          status: 'error',
        });
      });
  };

  const handleCancel = () => {
    if (initialSettings) {
      setNotesList(initialSettings.noteTypes);
      setIsApprovalRequired(initialSettings.isApprovalRequired);
      setAutoApproveDays(initialSettings.autoApproveDays);
    }
    setIsCancelConfirmModalOpen(false);
  };

  const isDirty = useMemo(() => {
    if (!initialSettings) return false;
    return (
      JSON.stringify(notesList) !== JSON.stringify(initialSettings.noteTypes) ||
      isApprovalRequired !== initialSettings.isApprovalRequired ||
      autoApproveDays !== initialSettings.autoApproveDays
    );
  }, [notesList, isApprovalRequired, autoApproveDays, initialSettings]);

  return (
    <Box display="flex" flexDirection="column" height="calc(100vh - 128px)">
      <Box height="calc(100vh - 20px)" overflowY="auto">
        <NotesSection notesList={notesList} setNotesList={setNotesList} isLoading={isLoading} isError={isError} />
        {/* <ApprovalSection
          isApprovalRequired={isApprovalRequired}
          setIsApprovalRequired={setIsApprovalRequired}
          autoApproveDays={autoApproveDays}
          setAutoApproveDays={setAutoApproveDays}
          isLoading={isLoading}
          isError={isError}
        /> */}
      </Box>
      {!isError && !isLoading && (
        <FooterSection
          onSave={handleSave}
          onCancel={() => {
            if (isDirty) {
              setIsCancelConfirmModalOpen(true);
            } else {
              handleCancel();
            }
          }}
          isSaveDisabled={!isDirty}
        />
      )}
      {isCancelConfirmModalOpen && (
        <CancelConfirmationModal
          isOpen={isCancelConfirmModalOpen}
          onClose={() => setIsCancelConfirmModalOpen(false)}
          onContinue={handleCancel}
        />
      )}
    </Box>
  );
};

export default DisciplinaryActionsSettings;
